import { initContract } from '@ts-rest/core'
import z from 'zod'
import { ErrorResponseSchema } from '../../shared/types'
import { JobStatus } from '../order/types'
import { JobReviewDTOSchema } from './job-review.schemas'
import {
  AssignFacilitySchema,
  BoxArrangementInputSchema,
  BoxArrangementResultSchema,
  BoxResponseSchema,
  BoxTrackerBoxSchema,
  FulfilmentInput,
  FulfilmentSchema,
  GetBoxesByOrderResponseSchema,
  GetFulfilmentOrderIdsQuerySchema,
  GetFulfilmentOrderIdsResponseSchema,
  GetFulfilmentsForCustomerIdBackfillQuerySchema,
  GetFulfilmentsForCustomerIdBackfillResponseSchema,
  GetJobsWithMissingDispatchCutoffQuerySchema,
  GetJobsWithMissingDispatchCutoffResponseSchema,
  HasStartedFulfilmentSchema,
  JobPatchSchema,
  JobSchema,
  OperationsStatsTeamLeaderSchema,
  RequeueJobsResponseSchema,
  ToggleHoldStatusSchema,
  UpdateBoxContentsRequestSchema,
  UpdateBoxContentsResponseSchema,
  UpdateBoxSerialNumberSchema,
  UpdateFulfilmentSchema,
} from './schemas'

const c = initContract()

const options = {
  pathPrefix: '/api/v1',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    409: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

export const fulfilmentServiceContract = c.router({
  createFulfilmentJob: {
    method: 'POST',
    path: '/fulfilments/facilities/:facilityId',
    pathParams: z.object({
      facilityId: z.number(),
    }),
    body: FulfilmentInput,
    responses: {
      200: AssignFacilitySchema,
    },
  },
  getBoxesByOrderId: {
    method: 'GET',
    path: '/jobs/by-order/:orderId',
    pathParams: z.object({
      orderId: z.string().uuid(),
    }),
    query: z.object({
      excludeCancelled: z.string().optional(),
      includeFulfilmentDetails: z.string().optional(),
    }),
    responses: {
      200: GetBoxesByOrderResponseSchema.array(),
    },
  },
  getJobsByFulfilmentId: {
    method: 'GET',
    path: '/jobs/by-fulfilment/:fulfilmentId',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    query: z.object({
      excludeCancelled: z.string().optional(),
    }),
    responses: {
      200: JobSchema.array(),
    },
  },
  deleteFulfilment: {
    method: 'DELETE',
    path: '/fulfilments/:fulfilmentId',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    responses: {
      204: null,
    },
  },
  checkFulfilmentHasStarted: {
    method: 'GET',
    path: '/fulfilments/:fulfilmentId/has-started',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    responses: {
      200: HasStartedFulfilmentSchema,
    },
  },
  toggleFulfilmentHoldStatus: {
    method: 'PATCH',
    path: '/fulfilments/:fulfilmentId/hold-status',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    body: ToggleHoldStatusSchema,
    responses: {
      200: FulfilmentSchema,
    },
  },
  removeJobsFromQueues: {
    method: 'DELETE',
    path: '/jobs/fulfilments/:fulfilmentId/queue-jobs',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    responses: {
      200: null,
    },
  },
  requeueJobs: {
    method: 'POST',
    path: '/jobs/fulfilments/:fulfilmentId/requeue-jobs',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    body: z.object({}),
    responses: {
      200: RequeueJobsResponseSchema,
    },
  },
  updateFulfilment: {
    method: 'PATCH',
    path: '/fulfilments/:fulfilmentId',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    body: UpdateFulfilmentSchema,
    responses: {
      200: FulfilmentSchema,
    },
  },
  updateBoxSerialNumber: {
    method: 'PATCH',
    path: '/jobs/by-box/:boxId/serial-number',
    pathParams: z.object({
      boxId: z.string().uuid(),
    }),
    body: UpdateBoxSerialNumberSchema,
    responses: {
      200: BoxResponseSchema,
    },
  },
  updateBoxContents: {
    method: 'PATCH',
    path: '/jobs/by-box/:boxId/contents',
    pathParams: z.object({
      boxId: z.string().uuid(),
    }),
    body: UpdateBoxContentsRequestSchema,
    responses: {
      200: UpdateBoxContentsResponseSchema,
    },
  },
  updateJobStatus: {
    method: 'PATCH',
    path: '/jobs/:jobId/status',
    pathParams: z.object({
      jobId: z.number(),
    }),
    body: z.object({
      status: z.nativeEnum(JobStatus),
    }),
    responses: {
      204: null,
    },
  },
  patchJob: {
    method: 'PATCH',
    path: '/jobs/:jobId',
    pathParams: z.object({
      jobId: z.number(),
    }),
    body: JobPatchSchema,
    responses: {
      204: null,
    },
  },
  replaceBoxArrangement: {
    method: 'PUT',
    path: '/fulfilments/:fulfilmentId/box-arrangement',
    pathParams: z.object({
      fulfilmentId: z.string().uuid(),
    }),
    body: BoxArrangementInputSchema,
    responses: {
      200: BoxArrangementResultSchema,
    },
  },
  getOperationsStatsTeamLeader: {
    method: 'GET',
    path: '/operations-stats/daily/team-leader',
    query: z.object({
      makeDate: z.string().describe('Date in YYYY-MM-DD format'),
      facilityId: z.coerce.number().describe('Facility ID (1=WP, 2=DS)'),
    }),
    responses: {
      200: OperationsStatsTeamLeaderSchema,
    },
  },
  getBoxTrackerList: {
    method: 'GET',
    path: '/operations-stats/box-tracker-list',
    query: z.object({
      makeDate: z.string().describe('Date in YYYY-MM-DD format'),
      facilityId: z.coerce.number().optional().describe('Facility ID (1=WP, 2=DS)'),
    }),
    responses: {
      200: z.array(BoxTrackerBoxSchema),
    },
  },
  getOrderIdsByFacilityAndDate: {
    method: 'GET',
    path: '/jobs/order-ids',
    summary: 'Get distinct order IDs with active fulfilment jobs for a facility and make date',
    description: 'Returns order IDs that have non-cancelled jobs at the given facility on the given make date. Used by reconciliation to verify paid orders have fulfilment jobs.',
    query: GetFulfilmentOrderIdsQuerySchema,
    responses: {
      200: GetFulfilmentOrderIdsResponseSchema,
    },
  },
  getFulfilmentsForCustomerIdBackfill: {
    method: 'GET',
    path: '/fulfilments/for-customer-id-backfill',
    summary: 'Page fulfilments for customer_id backfill (OPS-3212)',
    description: 'Returns fulfilments joined to a representative job order_id, cursor-paginated by fulfilment_id ASC. Used by orchestration to backfill fulfilments.customer_id from orders.user_id.',
    query: GetFulfilmentsForCustomerIdBackfillQuerySchema,
    responses: {
      200: GetFulfilmentsForCustomerIdBackfillResponseSchema,
    },
  },
  getJobsWithMissingDispatchCutoff: {
    method: 'GET',
    path: '/jobs/missing-dispatch-cutoff',
    summary: 'Get fulfilment jobs with null dispatch_time_utc in a make date window',
    description: 'Returns pre-pack, held and paused jobs whose dispatch_time_utc is null, with make dates falling within the given Melbourne calendar date range. Eligible statuses are listed in MISSING_DISPATCH_CUTOFF_SWEEP_JOB_STATUSES.',
    query: GetJobsWithMissingDispatchCutoffQuerySchema,
    responses: {
      200: GetJobsWithMissingDispatchCutoffResponseSchema,
    },
  },
  getJobReview: {
    method: 'GET',
    path: '/jobs/:jobId/review',
    pathParams: z.object({ jobId: z.coerce.number().int().positive() }),
    responses: {
      200: JobReviewDTOSchema,
    },
    summary: 'Get the box-review projection (expected vs packed) for a fulfilment job',
  },
}, options)
